import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import Button from "../../../../components/button/Button";
import { getCampaignDetail, createCampaign, updateCampaign, uploadCampaignFile } from "../../../../utils/apis/services/api";
import "./campaignForm.scss";

const COUNTRY_OPTIONS = [
  { value: "jamaica", label: "Jamaica" },
  { value: "usa", label: "USA" },
  { value: "india", label: "India" },
  { value: "can", label: "Canada" }
];

const DAYS = [
  { dayName: "Mo", dayValue: "monday" },
  { dayName: "Tu", dayValue: "tuesday" },
  { dayName: "We", dayValue: "wednesday" },
  { dayName: "Th", dayValue: "thursday" },
  { dayName: "Fr", dayValue: "friday" },
  { dayName: "Sa", dayValue: "saturday" },
  { dayName: "Su", dayValue: "sunday" }
];

const LinkBased = ({ id }) => {
  const history = useHistory();
  const [formValue, setFormValue] = useState({
    name: "",
    bannerRedirecturl: "",
    country: "",
    state_id: "",
    city_id: "",
    startDate: "",
    endDate: "",
    description: ""
  });
  const [selectedDays, setSelectedDays] = useState([]);
  const [uploadedImage, setUploadedImage] = useState("");
  const [uploadedImageId, setUploadedImageId] = useState("");

  useEffect(() => {
    if (id !== "Create") loadCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCampaign = async () => {
    let res = await getCampaignDetail(id);
    if (!res || !res.data || (res.status !== 200 && res.status !== 201)) return;
    let d = res.data.ResponseData?.response ?? res.data.response ?? res.data.ResponseData ?? res.data;
    if (d && typeof d === "object" && !d.name && !d.banner_redirect_url && d.response) d = d.response;
    setFormValue({
      name: d?.name ?? "",
      bannerRedirecturl: d?.banner_redirect_url ?? d?.bannerRedirecturl ?? "",
      country: d?.country ?? "",
      state_id: d?.state_id ?? "",
      city_id: d?.city_id ?? "",
      startDate: d?.start_date ?? d?.startDate ?? "",
      endDate: d?.end_date ?? d?.endDate ?? "",
      description: d?.description ?? ""
    });
    let days = d?.weekday ?? d?.weekdays ?? [];
    setSelectedDays(Array.isArray(days) ? days.map((w) => (w && typeof w === "object" ? (w.slug ?? w.name ?? w) : w)) : []);
    let img = d?.banner_image ?? d?.bannerImage;
    setUploadedImage(img?.file ?? img?.url ?? d?.banner_image_url ?? "");
    setUploadedImageId(img?.id ?? (typeof d?.banner_image === "number" ? d.banner_image : ""));
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormValue((p) => ({ ...p, [name]: value }));
  };

  const selectDays = (val) => {
    setSelectedDays((p) => (p.includes(val) ? p.filter((d) => d !== val) : [...p, val]));
  };

  const onDrop = useCallback(async (files) => {
    if (files.length === 0) return;
    let fd = new FormData();
    fd.append("file", files[0]);
    let res = await uploadCampaignFile(fd);
    if (!res || !res.data) {
      toast.error(res?.data?.response || "Upload failed");
      return;
    }
    let raw = res.data.response || res.data.ResponseData || res.data;
    let item = Array.isArray(raw) ? raw[0] : raw;
    if (item && (item.id != null || item.file || item.url)) {
      if (item.id != null) setUploadedImageId(item.id);
      setUploadedImage(item.file || item.url || item.banner_image_url || "");
    } else {
      toast.error("Upload failed");
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
    maxSize: 5 * 1024 * 1024
  });

  const validate = () => {
    if (!formValue.name) return "Name required";
    if (!formValue.bannerRedirecturl) return "Banner URL required";
    if (selectedDays.length === 0) return "Select week days";
    if (!formValue.startDate) return "Start date required";
    if (!formValue.endDate) return "End date required";
    if (!uploadedImageId) return "Banner image required";
    return "";
  };

  const handleSubmit = (e) => {
    e && e.preventDefault();
    let err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    let body = {
      name: formValue.name,
      banner_image: uploadedImageId,
      banner_redirect_url: formValue.bannerRedirecturl,
      country: formValue.country,
      start_date: formValue.startDate,
      end_date: formValue.endDate,
      description: formValue.description,
      weekday: selectedDays,
      banner_type: "linked-based",
      is_active: true,
      category: [],
      product: [],
      merchant: []
    };
    const run = async () => {
      let res = id === "Create" ? await createCampaign(body) : await updateCampaign(id, body);
      if (res && (res.status === 200 || res.status === 201)) {
        toast.success(res.data?.response || "Saved");
        history.push("/campaign");
      } else {
        toast.error(res?.data?.response || "Failed");
      }
    };
    run();
  };

  const goBack = () => history.goBack();

  return (
    <div className="campaign-form">
      <div className="campaign-form-back" onClick={goBack}>
        <i className="fa fa-arrow-left" /> Listings
      </div>
      <form onSubmit={handleSubmit} onKeyDown={(e) => e.key === "Enter" && handleSubmit(e)}>
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Campaign name *</label>
            <input name="name" value={formValue.name} onChange={handleInput} required />
          </div>
          <div className="campaign-form-group">
            <label>Enter link *</label>
            <input name="bannerRedirecturl" value={formValue.bannerRedirecturl} onChange={handleInput} type="url" required />
          </div>
        </div>
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Country</label>
            <select name="country" value={formValue.country} className="form-control bg-white" onChange={handleInput}>
              <option value="">Select</option>
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="campaign-form-group">
            <label>Select days *</label>
            <div className="days-select">
              {DAYS.map((d) => (
                <span
                  key={d.dayValue}
                  className={selectedDays.includes(d.dayValue) ? "day active" : "day"}
                  onClick={() => selectDays(d.dayValue)}
                >
                  {d.dayName}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Start Date *</label>
            <input
              type="date"
              name="startDate"
              value={formValue.startDate}
              onChange={handleInput}
              required
            />
          </div>
          <div className="campaign-form-group">
            <label>End Date *</label>
            <input type="date" name="endDate" value={formValue.endDate} onChange={handleInput} required />
          </div>
        </div>
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Description</label>
            <textarea name="description" value={formValue.description} onChange={handleInput} rows={4} />
          </div>
        </div>
        <div className="campaign-form-row">
          <div className="campaign-form-group">
            <label>Banner image *</label>
            <div className="banner-upload">
              {uploadedImage && (
                <div className="banner-preview">
                  <img src={uploadedImage} alt="" />
                </div>
              )}
              <div {...getRootProps()} className="banner-drop">
                <input {...getInputProps()} />
                <i className="fa fa-plus" /> Add Photo
              </div>
            </div>
          </div>
        </div>
        <div className="campaign-form-actions">
          <Button type="button" title="Cancel" onClick={goBack} />
          <button type="submit" className="btn-primary">
            {id === "Create" ? "Create" : "Update"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LinkBased;
